<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { assignOrderTypeToServiceAction } from '@/api/functions/services/assign-order-type-to-service.action.ts'
import { deassignOrderTypeFromServiceAction } from '@/api/functions/services/deassign-order-type-from-service.action.ts'
import { getServiceBySlugAction } from '@/api/functions/services/get-service-by-slug.action.ts'
import { toggleServiceStatusAction } from '@/api/functions/services/toggle-service-status.action.ts'
import type { Service, ServicePlan } from '@/api/typings/service.typing.ts'
import { useMutationApi, useQueryApi } from '@/composables/useApi.ts'
import { useGeneralSettings } from '@/composables/useGeneralSettings.ts'
import { useOrderTypes, useServiceCategories, useServiceProviders, useServiceTypes } from '@/composables/useServices.ts'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils.ts'
import ServicePlans from './partials/ServicePlans.vue'
import UpdateServiceModal from './partials/UpdateServiceModal.vue'

const route = useRoute()
const toast = useToast()

const showStatusModal = ref(false)
const showEditModal = ref(false)
const editService = ref<Service | null>(null)
const selectedOrderTypes = ref<string[]>([])

const serviceSlug = computed(() => route.params.slug as string)

const { categoryOptions } = useServiceCategories()

const { serviceTypeOptions } = useServiceTypes()

const { orderTypeOptions } = useOrderTypes()

const { providerOptions } = useServiceProviders()
const { currencies, countries } = useGeneralSettings()

const { data: serviceResponse, refetch } = useQueryApi({
  queryKey: computed(() => ['service', serviceSlug.value]),
  queryFn: () => getServiceBySlugAction(serviceSlug.value),
  enabled: computed(() => !!serviceSlug.value),
})

const service = computed(() => serviceResponse.value?.data)

const categoryLabel = computed(() => service.value?.categories?.map((category) => category.name).join('، ') || '—')

const currentStatus = computed(() => statusMeta[service.value?.is_active ? 'ACTIVE' : 'INACTIVE'])

const statusMeta = {
  ACTIVE: {
    label: 'فعال',
    class: 'bg-[#e6fff0] text-[#2ed06e]',
  },
  INACTIVE: {
    label: 'غیرفعال',
    class: 'bg-[#fde7ef] text-[#ed145b]',
  },
}

const isServiceActive = computed(() => !!service.value?.is_active)

const statusModalTitle = computed(() => (isServiceActive.value ? 'غیرفعال کردن سرویس' : 'فعال کردن سرویس'))

const statusModalMessage = computed(() =>
  isServiceActive.value ? 'آیا از غیرفعال کردن این سرویس اطمینان دارید؟' : 'آیا از فعال کردن این سرویس اطمینان دارید؟'
)

const statusModalConfirmText = computed(() => (isServiceActive.value ? 'بله، غیرفعال شود' : 'بله، فعال شود'))

const onEditService = () => {
  if (!service.value) return

  editService.value = {
    ...service.value,
  }

  showEditModal.value = true
}

const handleServiceEdit = () => {
  refetch()
  toast.success('سرویس با موفقیت ویرایش شد')
}

const toggleServiceStatusMutation = useMutationApi({
  mutationFn: (payload: { is_active: boolean }) => toggleServiceStatusAction(serviceSlug.value, payload),

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const onToggleServiceStatus = () => {
  if (!service.value) return

  showStatusModal.value = true
}

const handleServiceStatus = async () => {
  if (!service.value) {
    toast.error('سرویسی انتخاب نشده است', 'خطا')
    return
  }

  const isActive = !service.value.is_active

  try {
    await toggleServiceStatusMutation.mutateAsync({
      is_active: isActive,
    })

    showStatusModal.value = false

    await refetch()

    toast.success(isActive ? 'سرویس فعال شد' : 'سرویس غیرفعال شد')
  } catch {}
}

const assignOrderTypeMutation = useMutationApi({
  mutationFn: (orderTypeId: string) => assignOrderTypeToServiceAction(serviceSlug.value, orderTypeId),
})

const deassignOrderTypeMutation = useMutationApi({
  mutationFn: (orderTypeId: string) => deassignOrderTypeFromServiceAction(serviceSlug.value, orderTypeId),
})

const isSavingOrderTypes = computed(
  () => assignOrderTypeMutation.isPending.value || deassignOrderTypeMutation.isPending.value
)

const handleOrderTypesSave = async () => {
  if (!service.value || isSavingOrderTypes.value) return

  const currentIds = service.value.order_types?.map((item) => item.id) ?? []
  const selectedIds = selectedOrderTypes.value

  const toAssign = selectedIds.filter((id) => !currentIds.includes(id))
  const toDeassign = currentIds.filter((id) => !selectedIds.includes(id))

  if (!toAssign.length && !toDeassign.length) {
    toast.error('تغییری در نوع سفارش‌ها ایجاد نشده است')
    return
  }

  try {
    await Promise.all([
      ...toAssign.map((id) => assignOrderTypeMutation.mutateAsync(id)),
      ...toDeassign.map((id) => deassignOrderTypeMutation.mutateAsync(id)),
    ])

    await refetch()
    toast.success('نوع سفارش‌های سرویس با موفقیت به‌روزرسانی شد')
  } catch (error) {
    toast.error(getErrorMessage(error as Error), 'خطا')
    await refetch()
  }
}

const openServicePage = () => {
  if (!service.value?.slug) return

  const baseUrl = import.meta.env.VITE_SITE_URL

  if (!baseUrl) {
    toast.error('آدرس سایت تنظیم نشده است', 'خطا')
    return
  }

  window.open(`${baseUrl.replace(/\/$/, '')}/services/${service.value.slug}`, '_blank', 'noopener,noreferrer')
}

watch(
  () => service.value?.order_types,
  (orderTypes) => {
    selectedOrderTypes.value = orderTypes?.map((item) => item.id) ?? []
  },
  { immediate: true }
)
</script>

<template>
    <div class="mb-9">
        <ConfirmModal v-model="showStatusModal" :title="statusModalTitle" :message="statusModalMessage"
            :confirm-text="statusModalConfirmText" cancel-text="بازگشت" @confirm="handleServiceStatus"
            @cancel="showStatusModal = false" />

        <UpdateServiceModal v-model="showEditModal" :serviceToEdit="editService" :category-options="categoryOptions"
            :serviceTypeOptions="serviceTypeOptions" :categories="categoryOptions" :orderTypeOptions="orderTypeOptions"
            :providerOptions="providerOptions" @confirm="handleServiceEdit" @cancel="editService = null" />

        <router-link to="/services"
            class="w-fit mb-6 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
            <img src="/svg/back-icon.svg" class="me-1" width="22" />
            <span>بازگشت</span>
        </router-link>

        <div class="flex flex-col lg:flex-row lg:items-center justify-between mb-9 gap-4">
            <div class="w-full lg:w-auto flex flex-col lg:flex-row lg:items-center gap-3">

                <div class="w-full lg:w-auto flex flex-row items-center gap-2">
                    <div class="text-[#2d5396] text-sm">
                        شناسه سرویس:
                        <span class="font-fa text-[#1b325a]">
                            {{ service?.code }}
                        </span>
                    </div>
                    <div class="w-fit flex items-center justify-center text-[13px] font-medium h-10 rounded-[10px] px-5"
                        :class="currentStatus?.class">
                        {{ currentStatus?.label }}
                    </div>



                </div>
            </div>

            <div class="flex items-center gap-4">
                <Button :isAdmin="true" @click="openServicePage"
                    class="w-1/2 lg:w-auto px-3 rounded-[10px] h-10! py-0 text-[13px] font-normal">
                    <span class="text-nowrap flex items-center justify-center gap-2">
                        <img width="18" src="/svg/eye.svg" class="brightness-0 invert" />
                        مشاهده در سایت
                    </span>
                </Button>
                <Button :isAdmin="true" @click="onEditService"
                    class="w-1/2 lg:w-auto px-3 rounded-[10px] h-10! py-0 text-[13px] font-normal">
                    <span class="text-nowrap flex items-center justify-center gap-2">
                        <img width="20" src="/svg/edit-icon.svg" />
                        ویرایش سرویس
                    </span>
                </Button>

                <Button :isAdmin="true" variant="outline" @click="onToggleServiceStatus"
                    class="w-1/2 lg:w-auto px-3 rounded-[10px] h-10! py-0 text-[13px] font-normal">
                    <span class="text-nowrap flex items-center justify-center">
                        {{ service?.is_active ? 'غیرفعال کردن' : 'فعال کردن' }}
                    </span>
                </Button>
            </div>
        </div>

        <div class="w-full flex flex-col p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl">
            <div class="flex flex-col lg:flex-row justify-between gap-6 lg:gap-[168.73px]">
                <div class="w-full lg:w-1/2 flex flex-col gap-3">
                    <div class="w-full flex items-center justify-between text-sm font-medium">
                        <span class="text-[#2d5396]">نام سرویس:</span>
                        <span class="text-[#1b325a]">{{ service?.name }}</span>
                    </div>

                    <div class="w-full flex items-center justify-between text-sm font-medium">
                        <span class="text-[#2d5396]">اسلاگ:</span>
                        <span dir="ltr" class="font-fa text-[#1b325a]">{{ service?.slug }}</span>
                    </div>

                    <div class="w-full flex items-center justify-between text-sm font-medium">
                        <span class="text-[#2d5396]">ارائه‌دهنده:</span>
                        <span class="text-[#1b325a]">{{ service?.provider?.name }}</span>
                    </div>

                    <div class="w-full flex items-center justify-between text-sm font-medium">
                        <span class="text-[#2d5396]">دسته‌بندی:</span>
                        <span class="text-[#1b325a]">{{ categoryLabel }}</span>
                    </div>
                </div>

                <div class="w-full lg:w-1/2 flex flex-col gap-3">
                    <div class="w-full flex items-center justify-between text-sm font-medium">
                        <span class="text-[#2d5396]">وضعیت:</span>
                        <span :class="currentStatus.class" class="px-3 py-1 rounded-lg">
                            {{ currentStatus.label }}
                        </span>
                    </div>

                    <div class="w-full flex items-center justify-between text-sm font-medium">
                        <span class="text-[#2d5396]">تعداد پلن:</span>
                        <span class="font-fa text-[#1b325a]">{{ service?.plans.length }}</span>
                    </div>

                    <div class="w-full flex items-center justify-between text-sm font-medium">
                        <span class="text-[#2d5396]">شروع قیمت:</span>
                        <span class="font-fa text-[#1b325a]">
                            <span v-if="service?.start_from">$</span>
                            {{ service?.start_from ?? '—' }}
                        </span>
                    </div>
                </div>
            </div>
            <div v-if="service?.short_description"
                class="border-t border-[#eaeef5] pt-4 text-[13px] text-[#4e648c] font-medium mt-5">
                <span class="text-[#2d5396] me-1">توضیحات کوتاه:</span>
                {{ service.short_description }}
            </div>

            <div v-if="service?.description"
                class="border-t border-[#eaeef5] pt-4 text-[13px] text-[#4e648c] font-medium mt-4">
                <span class="text-[#2d5396] me-1">توضیحات:</span>
                <div v-html="service.description" class="service-description mt-2"></div>
            </div>

            <div v-if="service?.order_types?.length" class="border-t border-[#eaeef5] pt-8 mt-8 flex flex-col gap-6">
                <div class="text-[#2d5396] text-[13px] font-medium">
                    نوع سفارش
                </div>

                <div class="w-full lg:w-1/2 flex flex-col sm:flex-row sm:items-start gap-3">
                    <div class="flex-1">
                        <MultiSelect v-model="selectedOrderTypes" variant="checkbox" name="orderTypes"
                            label="انتخاب نوع سفارش" :options="orderTypeOptions" />
                    </div>

                    <Button :isAdmin="true" :disabled="isSavingOrderTypes" :loading="isSavingOrderTypes"
                        @click="handleOrderTypesSave" class="px-4 rounded-[10px] h-12! py-0 text-[13px] shrink-0">
                        {{ isSavingOrderTypes ? 'در حال ذخیره...' : 'ذخیره نوع سفارش‌ها' }}
                    </Button>
                </div>
            </div>

        </div>

        <ServicePlans :plans="service?.plans ?? []" :service-slug="service?.slug" :countries="countries"
            :currencies="currencies" @refetch="refetch" />

    </div>
</template>
